import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Check, ShieldCheck, Download, Printer, Eye, Brain, Heart, Clock } from 'lucide-react';
import { CtaButton } from '../components/CtaButton';
import { SocialProofPopup } from '../components/SocialProofPopup';
import { StickyCta } from '../components/StickyCta';
import { Testimonials } from '../components/Testimonials';
import { CHECKOUT_URL } from '../constants';
export function SalesPage() {
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-35">
      <SocialProofPopup data-id="element-36" />
      <StickyCta data-id="element-37" />

      {/* HEADER / HERO */}
      <header className="bg-gradient-to-b from-blue-900 to-blue-800 text-white pt-12 pb-16 px-4 text-center" data-id="element-38">
        <div className="max-w-4xl mx-auto" data-id="element-39">
          <p className="text-yellow-400 font-bold tracking-widest uppercase mb-4 text-sm md:text-base" data-id="element-40">
            MENTE EN ACCIÓN™
          </p>
          <h1 className="text-4xl md:text-6xl font-black leading-tight mb-6" data-id="element-41">
            500 Juegos para Mantener tu{' '}
            <span className="text-yellow-400" data-id="element-42">Mente Activa</span> y Disfrutar
            Cada Día
          </h1>

          <a href={CHECKOUT_URL} className="block max-w-3xl mx-auto mb-8 hover:scale-105 transition-transform duration-300" data-id="element-43">
            <img src="https://cdn.magicpatterns.com/uploads/7vAYjvgPCRRiu5duy295UT/bnn.png" alt="Mente en Acción - 500 Juegos" className="w-full rounded-xl shadow-2xl" data-id="element-44" />
          </a>

          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-blue-100" data-id="element-45">
            Más de 500 juegos imprimibles para ejercitar la memoria, la
            atención, la lógica y la concentración de una forma entretenida,
            sencilla y desde casa.
          </h2>

          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 mb-10 text-lg font-medium" data-id="element-46">
            <div className="flex items-center gap-2" data-id="element-47">
              <Eye className="text-yellow-400" data-id="element-48" /> Letra grande y fácil de leer
            </div>
            <div className="flex items-center gap-2" data-id="element-49">
              <Printer className="text-yellow-400" data-id="element-50" /> Listo para imprimir
            </div>
            <div className="flex items-center gap-2" data-id="element-51">
              <Download className="text-yellow-400" data-id="element-52" /> Acceso inmediato en PDF
            </div>
          </div>

          <div className="bg-red-600 text-white inline-block px-6 py-2 rounded-full font-bold text-xl mb-6 animate-pulse" data-id="element-53">
            HOY POR SOLO $12 USD
          </div>

          <CtaButton data-id="element-54" />
        </div>
      </header>

      {/* PROBLEM / IDENTIFICATION */}
      <section className="py-16 px-4 max-w-3xl mx-auto text-lg md:text-xl leading-relaxed" data-id="element-55">
        <p className="mb-6 font-bold text-2xl text-center text-red-600" data-id="element-56">
          ¿Sientes que un ser querido pasa demasiado tiempo frente al televisor
          sin una actividad que realmente lo mantenga entretenido?
        </p>
        <p className="mb-6" data-id="element-57">
          Con el paso de los años es normal buscar actividades que mantengan la
          mente ocupada y hagan los días más entretenidos.
        </p>
        <p className="mb-6" data-id="element-58">
          El problema es que la mayoría de opciones son aburridas, repetitivas o
          requieren usar tecnología complicada.
        </p>
        <p className="mb-10 font-bold text-2xl text-center text-blue-800" data-id="element-59">
          Por eso creamos MENTE EN ACCIÓN™.
        </p>
        <p className="mb-6" data-id="element-60">
          Un completo libro con más de 500 juegos cuidadosamente seleccionados
          para disfrutar un momento agradable mientras se ejercitan habilidades
          como la memoria, la atención y el razonamiento.
        </p>
        <p className="mb-10 font-bold text-center bg-yellow-100 p-4 rounded-lg" data-id="element-61">
          Todo con letra grande, diseño limpio y listo para imprimir.
        </p>

        <h3 className="text-2xl font-bold mb-6 text-center" data-id="element-62">
          Imagina que cada día puedas dedicar solo 15 minutos a una actividad
          entretenida…
        </h3>

        <ul className="space-y-4 mb-12" data-id="element-63">
          {['Resolver nuevos retos.', 'Descubrir juegos diferentes.', 'Compartir momentos en familia.', 'Crear una rutina agradable.', 'Disfrutar aprendiendo cada día.'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-64">
              <Check className="text-green-500 flex-shrink-0 w-6 h-6" data-id="element-65" />
              <span className="font-medium" data-id="element-66">{item}</span>
            </li>)}
        </ul>
        <p className="text-center font-bold text-xl mb-12" data-id="element-67">
          Todo desde la comodidad de casa.
        </p>

        <CtaButton text="SÍ, QUIERO ESTOS BENEFICIOS" data-id="element-68" />
      </section>

      {/* WHAT'S INSIDE */}
      <section className="py-16 bg-blue-50 px-4" data-id="element-69">
        <div className="max-w-4xl mx-auto" data-id="element-70">
          <h2 className="text-3xl md:text-5xl font-black text-center text-blue-900 mb-12 uppercase" data-id="element-71">
            ¿Qué encontrarás dentro?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-id="element-72">
            {['Sopas de letras gigantes', 'Encuentra las diferencias', 'Laberintos', 'Crucigramas fáciles', 'Juegos de lógica', 'Memoria visual', 'Asociación de imágenes', 'Secuencias', 'Buscar objetos', 'Acertijos', 'Juegos con números', 'Retos de concentración'].map((item, i) => <div key={i} className="bg-white p-4 rounded-lg shadow-sm border border-blue-100 flex items-center gap-3" data-id="element-73">
                <div className="bg-blue-100 p-2 rounded-full text-blue-600" data-id="element-74">
                  <Brain className="w-5 h-5" data-id="element-75" />
                </div>
                <span className="font-bold text-gray-700" data-id="element-76">{item}</span>
              </div>)}
          </div>
          <p className="text-center text-2xl font-bold text-blue-800 mb-12" data-id="element-77">
            Y mucho más… En total recibirás más de 500 actividades listas para
            imprimir.
          </p>

          <div className="bg-white p-8 rounded-2xl shadow-xl border-t-8 border-yellow-400" data-id="element-78">
            <h3 className="text-2xl font-bold mb-6 text-center" data-id="element-79">
              Diseñado especialmente para adultos
            </h3>
            <p className="text-center mb-6 text-gray-600" data-id="element-80">
              A diferencia de otros libros:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-81">
              {['Tiene letra grande.', 'Es cómodo de leer.', 'Los ejercicios son claros.', 'No necesitas internet.', 'Puedes imprimir solo las páginas que quieras.', 'Puedes repetir los juegos cuando desees.'].map((item, i) => <div key={i} className="flex items-center gap-2" data-id="element-82">
                  <Check className="text-green-500 w-5 h-5" data-id="element-83" />
                  <span data-id="element-84">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-16 px-4 max-w-4xl mx-auto" data-id="element-85">
        <h2 className="text-3xl md:text-5xl font-black text-center text-gray-900 mb-4 uppercase" data-id="element-86">
          Todo lo que recibirás hoy
        </h2>
        <p className="text-center text-xl text-red-600 font-bold mb-12" data-id="element-87">
          ¡Llevate estos bonos GRATIS por el día de hoy!
        </p>

        <div className="space-y-16" data-id="element-88">
          {/* Main Product */}
          <div className="text-center" data-id="element-89">
            <h3 className="text-2xl font-bold text-blue-800" data-id="element-90">
              Producto Principal
            </h3>
            <h4 className="text-4xl font-black mb-2" data-id="element-91">MENTE EN ACCIÓN™</h4>
            <p className="text-xl mb-2" data-id="element-92">Más de 500 juegos imprimibles.</p>
            <p className="text-gray-500 line-through text-lg" data-id="element-93">Valor: $49</p>
          </div>

          {/* Bono 1 */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200" data-id="element-94">
            <a href={CHECKOUT_URL} className="block" data-id="element-95">
              <img src="https://cdn.magicpatterns.com/uploads/iB8pbSFVwoXsUEjiAL3Wca/bn1.png" alt="Bono 1" className="w-full hover:opacity-90 transition-opacity" data-id="element-96" />
            </a>
            <div className="p-6 text-center" data-id="element-97">
              <div className="inline-block bg-red-600 text-white px-4 py-1 rounded-full font-bold text-sm mb-4" data-id="element-98">
                🎁 Bono 1
              </div>
              <h4 className="text-2xl font-bold mb-2" data-id="element-99">
                Calendario de Retos de 90 Días
              </h4>
              <p className="text-gray-600 mb-4" data-id="element-100">
                Crea el hábito de realizar una actividad cada día.
              </p>
              <p className="text-gray-400 line-through" data-id="element-101">Valor: $19</p>
            </div>
          </div>

          {/* Bono 2 */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200" data-id="element-102">
            <a href={CHECKOUT_URL} className="block" data-id="element-103">
              <img src="https://cdn.magicpatterns.com/uploads/hXSrmKVcboH1YYi2xLk8WB/bn2.png" alt="Bono 2" className="w-full hover:opacity-90 transition-opacity" data-id="element-104" />
            </a>
            <div className="p-6 text-center" data-id="element-105">
              <div className="inline-block bg-red-600 text-white px-4 py-1 rounded-full font-bold text-sm mb-4" data-id="element-106">
                🎁 Bono 2
              </div>
              <h4 className="text-2xl font-bold mb-2" data-id="element-107">
                100 Juegos Extra para Compartir en Familia
              </h4>
              <p className="text-gray-600 mb-4" data-id="element-108">
                Más momentos para disfrutar con hijos y nietos.
              </p>
              <p className="text-gray-400 line-through" data-id="element-109">Valor: $29</p>
            </div>
          </div>

          {/* Bono 3 */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200" data-id="element-110">
            <a href={CHECKOUT_URL} className="block" data-id="element-111">
              <img src="https://cdn.magicpatterns.com/uploads/gFPeANHZvMauw8WafBXiao/bn3.png" alt="Bono 3" className="w-full hover:opacity-90 transition-opacity" data-id="element-112" />
            </a>
            <div className="p-6 text-center" data-id="element-113">
              <div className="inline-block bg-red-600 text-white px-4 py-1 rounded-full font-bold text-sm mb-4" data-id="element-114">
                🎁 Bono 3
              </div>
              <h4 className="text-2xl font-bold mb-2" data-id="element-115">
                50 Acertijos para Conversar con Hijos y Nietos
              </h4>
              <p className="text-gray-600 mb-4" data-id="element-116">
                Diversión, conversación y recuerdos inolvidables.
              </p>
              <p className="text-gray-400 line-through" data-id="element-117">Valor: $19</p>
            </div>
          </div>

          {/* Bono 4 */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200" data-id="element-118">
            <a href={CHECKOUT_URL} className="block" data-id="element-119">
              <img src="https://cdn.magicpatterns.com/uploads/uPTbj54s87FrdGfQoAkfC4/bn4.png" alt="Bono 4" className="w-full hover:opacity-90 transition-opacity" data-id="element-120" />
            </a>
            <div className="p-6 text-center" data-id="element-121">
              <div className="inline-block bg-red-600 text-white px-4 py-1 rounded-full font-bold text-sm mb-4" data-id="element-122">
                🎁 Bono 4
              </div>
              <h4 className="text-2xl font-bold mb-2" data-id="element-123">
                Versión con Letra Gigante
              </h4>
              <p className="text-gray-600 mb-4" data-id="element-124">
                Máxima comodidad para leer sin esfuerzo.
              </p>
              <p className="text-gray-400 line-through" data-id="element-125">Valor: $29</p>
            </div>
          </div>

          {/* Bono 5 */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200" data-id="element-126">
            <a href={CHECKOUT_URL} className="block" data-id="element-127">
              <img src="https://cdn.magicpatterns.com/uploads/fRxzVjWZY7KUh2hVcfpgXo/bn55.png" alt="Bono 5 - Gimnasia Cerebral" className="w-full hover:opacity-90 transition-opacity" data-id="element-128" />
            </a>
            <div className="p-6 text-center" data-id="element-129">
              <div className="inline-block bg-red-600 text-white px-4 py-1 rounded-full font-bold text-sm mb-4" data-id="element-130">
                🎁 Bono 5
              </div>
              <h4 className="text-2xl font-bold mb-2" data-id="element-131">Gimnasia Cerebral</h4>
              <p className="text-gray-600 mb-4" data-id="element-132">
                Ejercicios divertidos para un cerebro más ágil y saludable.
              </p>
              <p className="text-gray-400 line-through" data-id="element-133">Valor: $15</p>
            </div>
          </div>
        </div>

        <div className="mt-16" data-id="element-134">
          <CtaButton text="QUIERO TODO ESTO AHORA" data-id="element-135" />
        </div>
      </section>

      <Testimonials data-id="element-136" />

      {/* PRICING / OFFER */}
      <section id="comprar" className="py-16 bg-gray-900 text-white px-4 scroll-mt-4" data-id="element-137">
        <div className="max-w-3xl mx-auto text-center" data-id="element-138">
          <h2 className="text-3xl font-bold mb-8" data-id="element-139">Resumen del valor total:</h2>

          <div className="bg-gray-800 rounded-xl p-6 mb-8 text-left max-w-md mx-auto space-y-3" data-id="element-140">
            <div className="flex justify-between border-b border-gray-700 pb-2" data-id="element-141">
              <span data-id="element-142">Producto principal</span> <span data-id="element-143">$49</span>
            </div>
            <div className="flex justify-between border-b border-gray-700 pb-2" data-id="element-144">
              <span data-id="element-145">Bono 1</span> <span data-id="element-146">$19</span>
            </div>
            <div className="flex justify-between border-b border-gray-700 pb-2" data-id="element-147">
              <span data-id="element-148">Bono 2</span> <span data-id="element-149">$29</span>
            </div>
            <div className="flex justify-between border-b border-gray-700 pb-2" data-id="element-150">
              <span data-id="element-151">Bono 3</span> <span data-id="element-152">$19</span>
            </div>
            <div className="flex justify-between border-b border-gray-700 pb-2" data-id="element-153">
              <span data-id="element-154">Bono 4</span> <span data-id="element-155">$29</span>
            </div>
            <div className="flex justify-between border-b border-gray-700 pb-2" data-id="element-156">
              <span data-id="element-157">Bono 5</span> <span data-id="element-158">$15</span>
            </div>
            <div className="flex justify-between pt-2 text-xl font-bold text-red-400" data-id="element-159">
              <span data-id="element-160">Valor total:</span> <span data-id="element-161">$160 USD</span>
            </div>
          </div>

          <div className="bg-gradient-to-b from-yellow-400 to-yellow-500 text-gray-900 rounded-3xl p-8 shadow-2xl transform md:scale-110 relative z-10" data-id="element-162">
            <h3 className="text-2xl font-black uppercase tracking-wider mb-2" data-id="element-163">
              Oferta Especial
            </h3>
            <p className="text-lg mb-2" data-id="element-164">
              Hoy no pagarás <span className="line-through" data-id="element-165">$160</span>.
            </p>
            <p className="text-lg mb-6" data-id="element-166">
              Ni siquiera pagarás <span className="line-through" data-id="element-167">$49</span>.
            </p>
            <p className="text-xl font-bold mb-4" data-id="element-168">
              Hoy puedes acceder al paquete completo por solo:
            </p>
            <div className="text-7xl font-black mb-6 text-red-600 drop-shadow-md" data-id="element-169">
              $12 <span className="text-3xl" data-id="element-170">USD</span>
            </div>

            <div className="flex flex-col items-center gap-2 mb-8 font-bold text-gray-800" data-id="element-171">
              <div className="flex items-center gap-2" data-id="element-172">
                <Check className="text-green-600" data-id="element-173" /> Pago único.
              </div>
              <div className="flex items-center gap-2" data-id="element-174">
                <Check className="text-green-600" data-id="element-175" /> Sin mensualidades.
              </div>
              <div className="flex items-center gap-2" data-id="element-176">
                <Check className="text-green-600" data-id="element-177" /> Acceso para siempre.
              </div>
              <div className="flex items-center gap-2" data-id="element-178">
                <Download className="text-green-600" data-id="element-179" /> Descarga inmediata.
              </div>
            </div>

            <CtaButton className="!max-w-full" data-id="element-180" />
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-16 px-4 bg-blue-50" data-id="element-181">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl p-8 md:p-12 shadow-xl border-4 border-blue-200 text-center flex flex-col items-center" data-id="element-182">
          <ShieldCheck className="w-24 h-24 text-blue-600 mb-6" data-id="element-183" />
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6 uppercase" data-id="element-184">
            Garantía de Satisfacción de 7 Días
          </h2>
          <p className="text-lg text-gray-700 mb-6" data-id="element-185">
            Queremos que realices tu compra con total tranquilidad.
          </p>
          <p className="text-lg text-gray-700 mb-6" data-id="element-186">
            Si durante los próximos 7 días consideras que el material no cumple
            con tus expectativas, solo escríbenos y te devolveremos el 100% de
            tu dinero.
          </p>
          <p className="font-bold text-xl text-green-600" data-id="element-187">
            Sin preguntas complicadas. Sin riesgos.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 max-w-3xl mx-auto" data-id="element-188">
        <h2 className="text-3xl md:text-4xl font-black text-center text-gray-900 mb-12 uppercase" data-id="element-189">
          Preguntas Frecuentes
        </h2>

        <div className="space-y-6" data-id="element-190">
          {[{
          q: '¿Es un libro físico?',
          a: 'No. Recibirás un archivo PDF descargable para imprimir las veces que desees.'
        }, {
          q: '¿Cómo lo recibo?',
          a: 'Inmediatamente después del pago podrás descargar todo el contenido.'
        }, {
          q: '¿Necesito internet para usarlo?',
          a: 'Solo para descargar el material. Después podrás imprimirlo y utilizarlo sin conexión.'
        }, {
          q: '¿Puedo imprimirlo varias veces?',
          a: 'Sí. Podrás imprimir únicamente las páginas que quieras y cuantas veces lo necesites.'
        }, {
          q: '¿En qué tamaño viene?',
          a: 'Formato A4 de alta calidad. Compatible con cualquier impresora doméstica o imprenta.'
        }].map((faq, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-191">
              <h3 className="text-xl font-bold text-blue-900 mb-2" data-id="element-192">{faq.q}</h3>
              <p className="text-gray-700" data-id="element-193">{faq.a}</p>
            </div>)}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 bg-gradient-to-b from-blue-800 to-blue-900 text-white px-4 text-center" data-id="element-194">
        <div className="max-w-3xl mx-auto" data-id="element-195">
          <h2 className="text-3xl md:text-5xl font-black mb-8" data-id="element-196">
            Hoy recibes TODO esto por solo…
          </h2>

          <div className="inline-block text-left mb-8 space-y-3 text-lg font-medium" data-id="element-197">
            <div className="flex items-center gap-3" data-id="element-198">
              <Check className="text-yellow-400" data-id="element-199" /> Más de 500 juegos.
            </div>
            <div className="flex items-center gap-3" data-id="element-200">
              <Check className="text-yellow-400" data-id="element-201" /> 5 Bonos Exclusivos.
            </div>
            <div className="flex items-center gap-3" data-id="element-202">
              <Check className="text-yellow-400" data-id="element-203" /> Acceso inmediato.
            </div>
            <div className="flex items-center gap-3" data-id="element-204">
              <Check className="text-yellow-400" data-id="element-205" /> Descarga en PDF.
            </div>
            <div className="flex items-center gap-3" data-id="element-206">
              <Check className="text-yellow-400" data-id="element-207" /> Garantía de 7 días.
            </div>
          </div>

          <p className="text-2xl mb-4" data-id="element-208">Todo por solo:</p>
          <div className="text-6xl font-black text-yellow-400 mb-10" data-id="element-209">
            $12 USD
          </div>

          <CtaButton text="QUIERO DESCARGAR MENTE EN ACCIÓN AHORA" data-id="element-210" />
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-500 text-center py-6 text-sm" data-id="element-211">
        <p data-id="element-212">
          © {new Date().getFullYear()} Mente en Acción. Todos los derechos
          reservados.
        </p>
        <p className="mt-2" data-id="element-213">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
          Además, este sitio NO está respaldado por Facebook de ninguna manera.
          FACEBOOK es una marca comercial de FACEBOOK, Inc.
        </p>
      </footer>
    </div>;
}